// src/modules/Shop/components/ProductCard.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaHeart, FaRegHeart, FaShoppingCart, FaEye } from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';

const Card = styled.article`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eee;
  transition: all 300ms;
  position: relative;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);

    .product-actions {
      opacity: 1;
      transform: translateY(0);
    }

    .product-image img {
      transform: scale(1.05);
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  background: #f8f8f8;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 500ms;
  }
`;

const Badges = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 2;
`;

const Badge = styled.span`
  background: ${props => props.$type === 'sale' ? '#ef4444' : props.$type === 'new' ? '#00D4AA' : '#111'};
  color: ${props => props.$type === 'new' ? '#000' : 'white'};
  font-size: 10px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const WishlistButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  background: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 2;
  transition: all 200ms;

  svg {
    font-size: 16px;
    color: ${props => props.$active ? '#ef4444' : '#999'};
  }

  &:hover {
    transform: scale(1.1);

    svg {
      color: #ef4444;
    }
  }
`;

const ProductActions = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transform: translateY(8px);
  transition: all 300ms;
  z-index: 2;
`;

const ActionButton = styled.button`
  flex: 1;
  background: ${props => props.$primary ? '#00D4AA' : 'white'};
  color: ${props => props.$primary ? '#000' : '#333'};
  border: none;
  padding: 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 200ms;

  &:hover {
    opacity: 0.9;
    transform: scale(1.02);
  }

  svg {
    font-size: 14px;
  }
`;

const Content = styled.div`
  padding: 16px;
`;

const Category = styled.span`
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Name = styled(Link)`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #111;
  text-decoration: none;
  margin: 8px 0;
  line-height: 1.4;

  &:hover {
    color: #00D4AA;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Price = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #111;
`;

const ComparePrice = styled.span`
  font-size: 14px;
  color: #999;
  text-decoration: line-through;
`;

const Discount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #ef4444;
`;

const Installments = styled.p`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const StockStatus = styled.span`
  display: inline-block;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  margin-top: 8px;
  background: ${props => props.$inStock ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  color: ${props => props.$inStock ? '#22c55e' : '#ef4444'};
`;

const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

const ProductCard = ({ product, onWishlistToggle, isWishlisted = false }) => {
  const [wishlisted, setWishlisted] = useState(isWishlisted);
  const { addItem } = useCart();

  const {
    id,
    name,
    slug,
    price,
    compare_price,
    images,
    category,
    stock,
    is_featured,
    created_at
  } = product;

  const discount = compare_price ? Math.round((1 - price / compare_price) * 100) : 0;
  const isNew = new Date(created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const inStock = stock > 0;
  const installments = Math.min(12, Math.floor(price / 100));
  const installmentValue = installments > 0 ? price / installments : price;

  const handleWishlist = (e) => {
    e.preventDefault();
    setWishlisted(!wishlisted);
    if (onWishlistToggle) {
      onWishlistToggle(product, !wishlisted);
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (inStock) {
      addItem(product);
    }
  };

  return (
    <Card>
      <ImageContainer className="product-image">
        <Link to={`/produto/${slug}`}>
          <img
            src={images?.[0] || 'https://placehold.co/400x400/f8f8f8/999?text=Produto'}
            alt={name}
          />
        </Link>

        <Badges>
          {discount > 0 && <Badge $type="sale">-{discount}%</Badge>}
          {isNew && <Badge $type="new">Novo</Badge>}
          {is_featured && <Badge $type="featured">Destaque</Badge>}
        </Badges>

        <WishlistButton $active={wishlisted} onClick={handleWishlist}>
          {wishlisted ? <FaHeart /> : <FaRegHeart />}
        </WishlistButton>

        <ProductActions className="product-actions">
          <ActionButton as={Link} to={`/produto/${slug}`}>
            <FaEye /> Ver
          </ActionButton>
          <ActionButton $primary onClick={handleAddToCart} disabled={!inStock}>
            <FaShoppingCart /> {inStock ? 'Comprar' : 'Esgotado'}
          </ActionButton>
        </ProductActions>
      </ImageContainer>

      <Content>
        {category?.name && <Category>{category.name}</Category>}

        <Name to={`/produto/${slug}`}>{name}</Name>

        <PriceContainer>
          <Price>{formatPrice(price)}</Price>
          {compare_price && compare_price > price && (
            <>
              <ComparePrice>{formatPrice(compare_price)}</ComparePrice>
              <Discount>-{discount}%</Discount>
            </>
          )}
        </PriceContainer>

        {installments > 1 && (
          <Installments>
            ou {installments}x de {formatPrice(installmentValue)} sem juros
          </Installments>
        )}

        <StockStatus $inStock={inStock}>
          {inStock ? `${stock} em estoque` : 'Produto esgotado'}
        </StockStatus>
      </Content>
    </Card>
  );
};

export default ProductCard;
